import { trigger } from '@angular/animations';
import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnDestroy,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import {
  AssetDto,
  AssetImageTypeDataDto,
  GalleryDto,
  GalleryFeedbackOptions,
  GalleryTheme,
  Section,
} from '@nusign/picflow-backend-types';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  shareReplay,
  Subject,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { fadeDownIfSubtle } from '../../../animations/fade-down-subtle.if.animation';
import { billingScopes } from '../../../billing-consts';
import { Rect } from '../../../classes/rect.class';
import { UploadFile, UploadObj } from '../../../classes/upload-obj.class';
import { defaultColorLabels } from '../../../configs/default-color-labels';
import { assetPaginationThreshold, specialCaseFormats } from '../../../consts';
import { IsVeryBottomPipe } from '../../../galleries/pipes/is-very-bottom.pipe';
import { AssetContextMenuComponent } from '../../../gallery-shared/components/asset-context-menu/asset-context-menu.component';
import { LayoutAssetComponent } from '../../../gallery-shared/components/layout-asset/layout-asset.component';
import { Filter } from '../../../gallery-shared/interfaces/filter.interface';
import { singleFilter } from '../../../gallery-shared/lib/filter-assets';
import { CanDownloadAssetPipe } from '../../../gallery-shared/pipes/can-download-asset.pipe';
import { GROWTHBOOK } from '../../../growthbook';
import { canDisplayAsset } from '../../../lib/can-display-asset';
import { includeIsLiked } from '../../../lib/include-is-liked';
import { objectsMatch } from '../../../lib/objects-match';
import { randomString } from '../../../lib/random-string';
import { sleep } from '../../../lib/sleep';
import { AiService } from '../../../services/ai.service';
import { AssetService } from '../../../services/asset.service';
import { ContextMenuService } from '../../../services/context-menu.service';
import { DialogService } from '../../../services/dialog.service';
import { DragDropService, DroppedEvent } from '../../../services/drag-drop.service';
import { GalleryThemeService } from '../../../services/gallery-theme.service';
import { GalleryUploadService } from '../../../services/gallery-upload.service';
import { PermissionService } from '../../../services/permission.service';
import { SectionService } from '../../../services/section.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { FromUrlFile } from '../../../services/upload-from-url.service';
import { UploadService } from '../../../services/upload.service';
import { DropComponent } from '../../../shared-extra/components/drop/drop.component';
import { StickyComponent } from '../../../shared-extra/components/sticky/sticky.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { DragItemDirective } from '../../../shared/components/drag-drop/drag-item.directive';
import { InfiniteScrollComponent } from '../../../shared/components/infinite-scroll/infinite-scroll.component';
import { SortingButtonComponent } from '../../../shared/components/sorting-button/sorting-button.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { InViewNewDirective } from '../../../shared/directives/in-view.directive-new';
import { InferredHoverDirective } from '../../../shared/directives/inferred-hover.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TransitionPreventorDirective } from '../../../shared/directives/transition-preventor.directive';
import { ShareAssetDialogComponent } from '../../dialogs/share-asset-dialog/share-asset-dialog.component';
import { calculateAssetMovePositions } from '../../lib/asset-move-position-calc';
import { getAssetSectionHeight } from '../../lib/get-asset-section-height';
import { adaptGalleryWidth } from '../../lib/get-gallery-width';
import { sortAssetSection } from '../../lib/sort-asset-section';
import { InferredContextOpenPipe } from '../../pipes/inferred-context-open.pipe';
import { LayoutGridService } from '../../services/layout-grid.service';
import { LayoutMosaicColumnsService } from '../../services/layout-mosaic-columns.service';
import { LayoutMosaicRowsService } from '../../services/layout-mosaic-rows.service';
import { AssetDownloadButtonComponent } from '../asset-download-button/asset-download-button.component';
import { AssetsSectionMenuComponent } from '../assets-section-menu/assets-section-menu.component';
import { SectionContainerComponent } from '../section-container/section-container.component';
import { UploadSourceMenuComponent } from '../upload-source-menu/upload-source-menu.component';

@Component({
  selector: 'app-assets-section',
  templateUrl: './assets-section.component.html',
  styleUrls: ['./assets-section.component.scss'],
  animations: [trigger('assetButton', fadeDownIfSubtle)],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DropComponent,
    SectionContainerComponent,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    ContextMenuComponent,
    AssetsSectionMenuComponent,
    InferredHoverDirective,
    AssetContextMenuComponent,
    RouterLink,
    AsyncPipe,
    StickyComponent,
    UploadSourceMenuComponent,
    LayoutAssetComponent,
    AssetDownloadButtonComponent,
    CanDownloadAssetPipe,
    InferredContextOpenPipe,
    InfiniteScrollComponent,
    InViewNewDirective,
    DragItemDirective,
    TransitionPreventorDirective,
    IsVeryBottomPipe,
    SortingButtonComponent,
  ],
})
export class AssetsSectionComponent implements OnDestroy {
  public growthBook = inject(GROWTHBOOK);
  private permissionService = inject(PermissionService);
  private layoutGrid = inject(LayoutGridService);
  private layoutRows = inject(LayoutMosaicRowsService);
  private layoutColumns = inject(LayoutMosaicColumnsService);
  private assetService = inject(AssetService);
  private uploadService = inject(UploadService);
  private galleryUploadService = inject(GalleryUploadService);
  private sectionService = inject(SectionService);
  private dialog = inject(DialogService);
  public state = inject(StateService);
  public contextService = inject(ContextMenuService);
  public galleryThemeService = inject(GalleryThemeService);
  private aiService = inject(AiService);
  private dragDropService = inject(DragDropService);
  private toasts = inject(ToastService);
  uploadIdPrefix = randomString(8);
  @ViewChild('assetGallery', { static: false }) assetGallery: ElementRef<HTMLElement>;
  @ViewChild('sectionContainer') sectionContainer: SectionContainerComponent;
  @ViewChild('sectionContainer', { read: ElementRef }) sectionContainerEl: ElementRef<HTMLElement>;
  @ViewChild('sectionContent') sectionContent: ElementRef<HTMLDivElement>;
  @ViewChild('upload') upload: ElementRef;
  readonly theme = input<GalleryTheme>();
  readonly darkBackground = input<boolean>(undefined);
  readonly section = input<Section>(undefined);
  readonly sectionIndex = input<number>(undefined);
  readonly gallery = input<GalleryDto>(undefined);
  readonly sections = input<Section[]>(undefined);
  readonly allowedAndEnabledFeedbackOptions = input<GalleryFeedbackOptions[]>(undefined);
  readonly galleryPermissions = input<GalleryDto['permissions']>();
  readonly filters = input<Filter[]>([]);

  onCoverChange = output<void>();
  onSectionDeleted = output<Section>();
  addSection = output<number>();

  sorting$ = toObservable(this.section).pipe(
    distinctUntilChanged((a, b) => a.id === b.id && a.sorting === b.sorting),
    map(section => section.sorting)
  );
  fetchMoreAssets$ = new BehaviorSubject<void>(undefined);
  addedAssets$ = new Subject<void>();
  destroyed$ = new Subject<void>();
  applyingSorting = signal(false);

  showFilenames = computed(() => {
    const section = this.section();
    const theme = this.theme();
    if (!section || !theme) return false;
    return section.show_filenames ?? theme.section.show_filenames;
  });

  private assetDroppedWatcher = this.dragDropService.dropped$
    .pipe(
      takeUntil(this.destroyed$),
      filter(dropped => {
        if (!dropped) return false;
        if (dropped.type !== 'asset') return false;
        if (!['asset', 'section', 'gallery'].includes(dropped.targetType)) return false;
        if (dropped.context === this.section()?.id) return true;
        if (dropped.sourceContext === this.section()?.id) return true;
        return false;
      }),
      switchMap(event => {
        const assetMovedInOrOut = event.context !== event.sourceContext;
        if (!assetMovedInOrOut) return of([event, undefined as AssetDto] as const);

        const sorting = this.section().sorting;
        if (sorting !== 'position') {
          return this.assetService.getAsset(event.id, this.gallery().id).pipe(
            take(1),
            map(asset => [event, asset] as const)
          );
        }
        const toast = this.allAssetsLoaded()
          ? undefined
          : this.toasts.add('Processing asset position changes. Please wait...', 'info', {
              duration: undefined,
            });
        return forkJoin([
          this.loadAllAssets().pipe(
            take(1),
            map(() => event)
          ),
          this.assetService.getAsset(event.id, this.gallery().id).pipe(take(1)),
        ]).pipe(
          tap(() => {
            if (toast) {
              this.toasts.close(toast);
            }
          })
        );
      })
    )
    .subscribe(([event, asset]) => {
      this.assetDropped(event, asset);
    });

  private sectionDroppedWatcher = this.dragDropService.dropped$
    .pipe(
      takeUntil(this.destroyed$),
      filter(dropped => {
        if (!dropped) return false;
        if (dropped.type !== 'asset') return false;
        if (dropped.targetType !== 'section') return false;
        return dropped.target === this.section()?.id;
      }),
      switchMap(event =>
        this.assetService.getAsset(event.id, this.gallery().id).pipe(
          take(1),
          map(asset => [event, asset] as const)
        )
      )
    )
    .subscribe(([event, asset]) => {
      this.dropOnEmptySection(event, asset);
    });

  readonly sortings = ['position', 'name', 'created_at', 'capture_time', 'id'] as const;
  readonly destroyed = toSignal(this.destroyed$.pipe(map(() => true)), { initialValue: false });

  readonly noGalleryAnimation = signal(true);
  readonly sectionHeight = computed(() => {
    const section = this.section();
    const theme = this.theme();
    const assetPositions = this.assetPositions();
    if (!section) return;

    if (!assetPositions) {
      return this.sectionService.sectionHeightCache[section.id];
    }

    const height = getAssetSectionHeight(
      assetPositions,
      section.spacing ?? theme.section.spacing,
      section.show_filenames ?? theme.section.show_filenames
    );
    this.sectionService.sectionHeightCache[section.id] = height;
    return height;
  });
  readonly processingNewAssets = signal(true);
  readonly allAssetsLoaded = signal(false);
  readonly lastAsset = signal<AssetDto | undefined>(undefined);
  readonly debugMode = signal(this.growthBook.isOn('asset-debug-mode'));
  readonly hoverAssetId = signal<string>(undefined);
  readonly inView = signal(new Map<string, boolean>());

  readonly draggingItem = toSignal(
    this.dragDropService.dragging$.pipe(map(dragging => (dragging?.triggered ? dragging : undefined)))
  );

  readonly draggingAssetId = toSignal(
    this.dragDropService.dragging$.pipe(
      map(dragging => {
        if (!dragging) return;
        if (!dragging.triggered) return;
        if (dragging.type !== 'asset') return;
        return dragging.id;
      })
    )
  );
  readonly dragHovering = toSignal(
    this.dragDropService.hovering$.pipe(
      map(hovering => {
        if (!hovering) return;
        if (hovering.type !== 'asset') return;

        const layout = this.section()?.grid_layout ?? this.theme()?.section.grid_layout;

        let dropPosition: 'before' | 'after';
        if (layout === 'columns') {
          const offsetY = hovering.position.y - hovering.targetBounds.y;
          dropPosition = offsetY > hovering.targetBounds.height / 2 ? 'after' : 'before';
        } else {
          const offsetX = hovering.position.x - hovering.targetBounds.x;
          dropPosition = offsetX > hovering.targetBounds.width / 2 ? 'after' : 'before';
        }

        return {
          ...hovering,
          dropPosition,
        };
      })
    )
  );

  readonly dontReactToHover = computed(() => {
    const section = this.section();
    const draggingItem = this.draggingItem();
    if (!draggingItem || !section) return;
    if (section.sorting === 'position') return false;
    if (draggingItem.context === section.id) return false;
    return true;
  });

  readonly dragHoverMoveBeforeAsset = computed(() => {
    const hovering = this.dragHovering();
    const draggingAsset = this.draggingAssetId();
    const layoutService = this.layoutService();
    const assets = this.filteredAssets();
    const assetPositions = this.assetPositions();
    if (!draggingAsset || !hovering || !layoutService || !assets || !assetPositions) return;
    if (draggingAsset === hovering.id) return;
    if (this.dontReactToHover()) return;

    if (hovering.dropPosition === 'after') {
      const index = assets.findIndex(a => a.id === hovering.id);
      const assetAfter = layoutService.getAssetAfter(hovering.id, assets, assetPositions, this.showFilenames());
      if (assetAfter?.id === draggingAsset) return;
      return assets[index];
    }

    const assetBefore = layoutService.getAssetBefore(hovering.id, assets, assetPositions, this.showFilenames());
    if (assetBefore?.id === draggingAsset) return;
    return assetBefore;
  });

  readonly dragHoverMoveAfterAsset = computed(() => {
    const hovering = this.dragHovering();
    const draggingAssetId = this.draggingAssetId();
    const layoutService = this.layoutService();
    const assets = this.filteredAssets();
    const assetPositions = this.assetPositions();
    if (!draggingAssetId || !hovering || !layoutService || !assets || !assetPositions) return;
    if (draggingAssetId === hovering.id) return;
    if (this.dontReactToHover()) return;

    if (hovering.dropPosition === 'before') {
      const index = assets.findIndex(a => a.id === hovering.id);
      const assetBefore = layoutService.getAssetBefore(hovering.id, assets, assetPositions, this.showFilenames());
      if (assetBefore?.id === draggingAssetId) return;
      return assets[index];
    }
    const assetAfter = layoutService.getAssetAfter(hovering.id, assets, assetPositions, this.showFilenames());
    if (assetAfter?.id === draggingAssetId) return;
    return assetAfter;
  });

  private assets$ = combineLatest([
    toObservable(this.section).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
    toObservable(this.gallery).pipe(filter(Boolean), distinctUntilKeyChanged('id')),
    toObservable(this.galleryPermissions).pipe(filter(Boolean), distinctUntilChanged(objectsMatch)),
    this.sorting$.pipe(
      map(sorting => sorting.includes('capture_time')),
      distinctUntilChanged()
    ),
  ]).pipe(
    switchMap(([section, gallery, permissions, captureTimeSorting]) => {
      // if this section has capture_time sorting, or if the whole gallery doens't have that many assets, just fetch them all instead of using pagination
      if (captureTimeSorting || gallery.assets_count < assetPaginationThreshold) {
        return this.assetService
          .getSectionAssets(section.id, gallery.id, {
            includeIsLiked: includeIsLiked(permissions, gallery),
          })
          .pipe(
            switchMap(assets => {
              return this.sorting$.pipe(
                map(sorting => {
                  assets = sortAssetSection(assets, sorting);
                  return {
                    assets,
                    has_more_next: false,
                  };
                })
              );
            })
          );
      }
      return this.assetService.getAssetsPaginated(section.id, gallery.id, this.sorting$, this.fetchMoreAssets$, {
        includeIsLiked: includeIsLiked(permissions, gallery),
      });
    }),
    map(resp => {
      if (!resp) return [];
      const assets = resp.assets;
      this.allAssetsLoaded.set(!resp.has_more_next);
      this.lastAsset.set(assets[assets.length - 1]);
      this.processingNewAssets.set(true);
      this.addedAssets$.next();
      return [...assets];
    }),
    shareReplay(1)
  );

  readonly assets = toSignal(this.assets$);

  assetsContainCaptureTime = computed(() => {
    const assets = this.assets();
    if (!assets || !assets.length) return false;
    return assets.some(asset => !!(asset.version_data?.data as AssetImageTypeDataDto)?.exif?.captureTime);
  });

  readonly filteredAssets = computed(() => {
    const assets = this.assets();
    const filters = this.filters();
    const galleryPermissions = this.galleryPermissions();
    if (!assets || !galleryPermissions) return;
    if (!filters || !filters.length) return assets;

    if (!galleryPermissions.can_review_if_authenticated) {
      return assets.filter(asset => asset.status !== 'rejected');
    }
    return assets.filter(asset => !filters.find(filter => singleFilter(filter, asset)));
  });

  private readonly uploads = toSignal(
    this.uploadService.uploads$.pipe(map(uploads => uploads.filter(upload => upload.sectionId === this.section()?.id)))
  );

  private readonly tempUploadAssets = computed(() => {
    const uploads = this.uploads();
    if (!uploads || !uploads.length) return [];

    return uploads
      .filter(upload => upload.previewRect)
      .map(upload => {
        return <AssetDto & { upload: UploadObj; type: 'preview' }>{
          id: upload.assetId || upload.assetToReplaceId || upload.id,
          section: upload.sectionId,
          gallery: upload.galleryId,
          position: upload.position,
          type: 'preview',
          name: upload.file.name.split('.').slice(0, -1).join('.'),
          version_data: {
            data: {
              width: upload.previewRect?.width ?? 0,
              height: upload.previewRect?.height ?? 0,
            },
          },
          upload,
        };
      });
  });

  readonly filteredAssetsAndUploads = computed(() => {
    const filteredAssets = this.filteredAssets();
    const uploadAssets = this.tempUploadAssets();
    if (!filteredAssets || !uploadAssets) return filteredAssets;
    if (!uploadAssets.length) return filteredAssets;

    return sortAssetSection(
      [
        ...filteredAssets.filter(a => a.version_data?.status !== 'pending'),
        ...uploadAssets.filter(
          uploadAsset =>
            !filteredAssets.some(asset => asset.id === uploadAsset.id && asset.version_data.status !== 'pending')
        ),
      ],
      this.section().sorting
    );
  });

  readonly assetQueryParams = computed(() => {
    const filtersValue = this.filters();
    const filters = filtersValue.length ? JSON.stringify(filtersValue) : undefined;
    return { filters, autoplay: true };
  });

  readonly colorLabels = computed(() => {
    const gallery = this.gallery();
    if (this.permissionService.canDisplayElement(billingScopes.asset.customLabels)) {
      return gallery.color_labels;
    }
    return defaultColorLabels;
  });

  readonly layoutService = computed(() => {
    const section = this.section();
    const theme = this.theme();
    if (!section || !theme) return;

    const layout = section.grid_layout ?? (theme.section.grid_layout as 'grid' | 'rows' | 'columns');
    if (layout === 'grid') {
      return this.layoutGrid;
    }
    if (layout === 'rows') {
      return this.layoutRows;
    }
    if (layout === 'columns') {
      return this.layoutColumns;
    }
    console.error('Unsupported layout', layout);
  });

  private assetPositionOverrides = signal<{ bounds: Rect; dimensions: Rect }[]>(undefined);
  readonly assetPositions = computed(() => {
    const assetPositionOverrides = this.assetPositionOverrides();
    const filteredAssets = this.filteredAssetsAndUploads();
    const dimensions = this.sectionDimensions();
    const layoutService = this.layoutService();
    const section = this.section();
    const theme = this.theme();
    if (assetPositionOverrides) return assetPositionOverrides;
    if (!filteredAssets || !layoutService || !section || !theme) return [];
    const positions = layoutService.calculateAssetPositions(section, theme, filteredAssets, dimensions);

    return positions;
  });

  readonly sectionDimensions = computed(() => {
    const assets = this.filteredAssetsAndUploads();
    const section = this.section();
    const theme = this.theme();
    const windowWidth = this.windowWidth();
    const layoutService = this.layoutService();

    if (!layoutService || !assets) return;
    return layoutService.calculateSectionDimensions(section, theme, assets, windowWidth);
  });

  readonly sectionWidth = computed(() => {
    const dimensions = this.sectionDimensions();
    const filteredAssets = this.filteredAssetsAndUploads();
    const section = this.section();
    const theme = this.theme();
    const windowWidth = this.windowWidth();

    if (!section || !theme || !dimensions) return;
    const sectionWidth = adaptGalleryWidth(section.width ?? theme.section.width, windowWidth);
    if (!filteredAssets) return sectionWidth;

    return Math.min(dimensions.galleryWidth, sectionWidth);
  });

  constructor() {
    effect(async () => {
      const assets = this.assets();
      if (!assets) return;
      await sleep(100);
      this.noGalleryAnimation.set(false);
      this.processingNewAssets.set(false);
    });
  }

  private aiUploadWatcher = this.aiService.onNewImages$
    .pipe(
      takeUntil(this.destroyed$),
      filter(data => data?.sectionId === this.section()?.id && !!data?.urls.length)
    )
    .subscribe(event => {
      const files = event.urls.map((url, i) => {
        const name = this.aiService.getFileName(event.params.model, i);

        return <FromUrlFile>{
          name,
          url,
          size: 100_000,
          source: 'url',
        };
      });

      this.uploadAssets(files);
    });

  @HostListener('window:resize')
  onResize() {
    this.windowWidth.set(window.innerWidth);
  }
  readonly windowWidth = signal(window.innerWidth);
  readonly isDesktop = computed(() => this.windowWidth() >= 900);

  setInView(assetId: string, inView: boolean) {
    this.inView.update(map => {
      map.set(assetId, inView);
      return map;
    });
  }

  async uploadAssetEvent(target: EventTarget) {
    const files = (target as HTMLInputElement).files;
    await this.uploadAssets(Array.from(files));
    if (this.upload) this.upload.nativeElement.value = '';
  }

  async uploadAssets(files: UploadFile[]) {
    if (!this.galleryPermissions().can_edit) return;
    if (!files.length) {
      this.toasts.add('No files selected', 'error', { duration: 3000 });
      return;
    }
    this.noGalleryAnimation.set(true);

    this.assetService
      .getSectionAssets(this.section().id, this.gallery().id)
      .pipe(take(1))
      .subscribe(allAssets => {
        this.galleryUploadService
          .autoAddTeaserAfterUpload(
            this.gallery(),
            this.uploadService.managedUpload(
              files,
              this.gallery(),
              this.section().id,
              allAssets,
              this.section().sorting
            )
          )
          .subscribe()
          .add(() => {
            this.noGalleryAnimation.set(false);
          });
      });
  }

  private loadAllAssets(): Observable<void> {
    if (this.allAssetsLoaded()) {
      return of(undefined);
    }
    this.fetchMoreAssets$.next();
    return this.addedAssets$.pipe(
      take(1),
      switchMap(() => this.loadAllAssets())
    );
  }

  downloadClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  public async changeAssetSorting(sortBy: string) {
    const currentSorting = this.section().sorting;
    if (sortBy === currentSorting.split('|')[0]) return;
    const section = this.section();
    this.sectionService.updateSection(section, { sorting: sortBy }).subscribe();
  }

  public async flipSorting() {
    const currentSorting = this.section().sorting;
    if (currentSorting.startsWith('-')) {
      this.changeAssetSorting(currentSorting.substring(1));
    } else {
      this.changeAssetSorting(`-${currentSorting}`);
    }
  }

  openShareDialog(asset: AssetDto) {
    this.dialog.open(ShareAssetDialogComponent, { gallery: this.gallery(), asset });
  }

  private allowDownload(asset: AssetDto) {
    if (this.galleryPermissions().can_edit) return true;
    const gallery = this.gallery();
    if (!gallery.download_single_asset) return false;
    if (gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  }

  assetRightClick(asset: AssetDto, event: Event) {
    if (this.allowDownload(asset)) return true;
    event.preventDefault();
    event.stopPropagation();
  }

  private supportsTransparency(asset: AssetDto) {
    const name = asset.version_data.asset_name;
    const extension = name.split('.').pop().toLowerCase();
    return specialCaseFormats.includes(extension);
  }

  withBg(asset: AssetDto) {
    if (!canDisplayAsset(asset)) return false;
    if (asset.type === 'image') {
      return !this.supportsTransparency(asset);
    }
    return asset.type !== 'non_preview' && asset.type !== 'audio';
  }

  dropOnEmptySection(event: DroppedEvent, asset: AssetDto) {
    this.assetService.updateAsset(asset, { section: this.section().id, position: 0 }, this.gallery().id);
  }

  private assetDropped(
    event: DroppedEvent,
    movedAsset?: AssetDto, // will be there if the asset was moved in or out of the section
    appliedSorting = false
  ) {
    if (event.id === event.target) return; // don't drop on itself
    const movingOut = event.context !== this.section().id;
    const movingIn = event.sourceContext !== this.section().id;

    const sorting = this.section().sorting;
    if (sorting !== 'position') {
      if (movingOut) return;
      if (movingIn) {
        this.assetService.updateAsset(movedAsset, { section: this.section().id }, this.gallery().id);
        // in this case, we don't need to switch to position sorting because they just dragged an asset in.
        // We assume that they want to keep the current sorting behaviour.
        return;
      }

      const toast = this.toasts.add('Sorting assets...', 'info', { duration: undefined });
      this.applyingSorting.set(true);
      this.assetService.applySorting(this.section().id, this.gallery().id, sorting).subscribe(() => {
        this.sectionService.updateSection(this.section(), { sorting: 'position' }).subscribe(() => {
          this.sorting$
            .pipe(
              filter(s => s === 'position'),
              take(1)
            )
            .subscribe(() => {
              this.applyingSorting.set(false);
              this.toasts.close(toast);
              this.assetDropped(event, movedAsset, true);
            });
        });
      });
      return;
    }

    const layout = (this.section()?.grid_layout ?? this.theme().section.grid_layout) as 'columns' | 'rows' | 'grid';
    const offsetX = event.position.x - event.targetBounds.x;
    const offsetY = event.position.y - event.targetBounds.y;

    const drop = movingOut
      ? 'out'
      : layout === 'columns'
        ? offsetY > event.targetBounds.height / 2
          ? 'after'
          : 'before'
        : offsetX > event.targetBounds.width / 2
          ? 'after'
          : 'before';

    const assets = this.filteredAssets();

    const changes = calculateAssetMovePositions(
      event.id,
      event.target,
      assets,
      this.assetPositions(),
      layout,
      drop,
      this.layoutService(),
      Number(event.sourceExtra),
      this.showFilenames()
    );

    // // debug code to see the new positions without applying them
    // const positions = this.assetPositions();
    // this.assetPositionOverrides.set(
    //   assets.map((asset, index) => {
    //     const change = changes.find(c => c.id === asset.id);
    //     const position = positions[index];
    //     return {
    //       bounds: change.newBounds,
    //       dimensions: new Rect(
    //         change.newBounds.x - (position.bounds.x - position.dimensions.x),
    //         change.newBounds.y - (position.bounds.y - position.dimensions.y),
    //         change.newBounds.width - (position.bounds.width - position.dimensions.width),
    //         change.newBounds.height - (position.bounds.height - position.dimensions.height)
    //       ),
    //     };
    //   })
    // );
    // return;

    const items = changes.map(change => {
      return {
        position: change.index,
        assetId: change.id,
        asset: assets.find(a => a.id === change.id),
      };
    });

    this.assetService
      .updateAssetPositions(
        items
          .filter(item => {
            if (!item.asset) return false;
            // We can't check if the position is already correct,
            // because the optimistic sorting will have already updated them
            if (!appliedSorting && item.asset.position === item.position) return false; // no change needed
            return true;
          })
          .map(change => {
            return {
              asset: change.asset,
              position: change.position,
            };
          })
      )
      .subscribe();

    items
      .filter(item => !item.asset && item.assetId === movedAsset?.id)
      .forEach(item => {
        return this.assetService.updateAsset(
          item.asset,
          { section: this.section().id, position: item.position },
          this.gallery().id
        );
      });
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
